import { z } from 'zod';
import { EnrollmentType } from './enrollment-type.js';
/**
 * ── ESI ID lookup → enriched meter detail (SHOP-1 / SHOP-3) ────────────────
 *
 * The storefront's meter-first redesign resolves ONE thing before it shows any
 * plan: the customer's ERCOT meter. There are two on-ramps, but they converge on
 * a single lookup and a single shape:
 *
 *   - ZIP path  — ZIP → address suggest → pick → `POST /bff/address/lookup`
 *                 resolves a candidate whose display `esiid` is then looked up here.
 *   - ESI ID path — the customer types their ESI ID directly.
 *
 * Both call `POST /bff/esiid/lookup` and receive the SAME `MeterDetail`, so the
 * SPA renders one `MeterSummaryCard` + one "see all details" dialog regardless of
 * the on-ramp.
 *
 * ⚠️ DUMMY-DATA STUB (SHOP-1). The BFF handler currently SYNTHESIZES a
 * deterministic `MeterDetail` from the input ESI ID (a demo mock, same posture as
 * `/bff/address/suggest`). The real handler fans out to GMO/ERCOT for these
 * fields (research: docs/us/research/esiid-meter-detail-fields.md). The SCHEMA is
 * the production shape; only the DATA behind it is stubbed. See the BFF STUBS.md.
 *
 * ── AC3 — NEVER ENUMERABLE ─────────────────────────────────────────────────
 * The `resolved` envelope carries an OPAQUE, session-scoped `candidateId` — the
 * redemption handle the funnel trusts. `MeterDetail.esiid` is DISPLAY-ONLY: a
 * confirmation of the customer's own meter, never a key any later hop trusts. A
 * customer typing their own ESI ID is a display/lookup INPUT (captcha- +
 * rate-limited, session-scoped), exactly like typing their own address — it is
 * resolved server-side, and the browser never holds it as a redemption key.
 */
/** Residential vs business premise — gates the (residential-only) plan shelf downstream. */
export const CustomerType = z.enum(['RESIDENTIAL', 'BUSINESS']);
/** ERCOT settlement / load zone. Drives wholesale/EFL pricing accuracy; shown for transparency. */
export const LoadZone = z.enum(['LZ_NORTH', 'LZ_HOUSTON', 'LZ_SOUTH', 'LZ_WEST']);
/** Physical meter class — AMS (smart), non-AMS (legacy/manual), IDR (interval / demand). */
export const MeterType = z.enum(['AMS', 'NON_AMS', 'IDR']);
/** Premise service status — drives SWITCH vs MOVE_IN and energization messaging. */
export const ServiceStatus = z.enum(['ACTIVE', 'INACTIVE']);
/** The structured, display service address the meter resolved to (Texas retail only). */
export const MeterAddressSchema = z.object({
    line1: z.string().min(1).max(120),
    unit: z.string().max(40).optional(),
    city: z.string().min(1).max(80),
    state: z.literal('TX'),
    zip: z.string().regex(/^\d{5}$/u, 'ZIP must be 5 digits'),
});
/**
 * The enriched meter-detail card (research §2). Every field here is DISPLAY —
 * the shopper's own resolved meter, shown back to them for recognition and to
 * drive the (later) plan filter. It carries NO redemption key: the opaque
 * `candidateId` rides the response envelope, not this object.
 */
export const MeterDetailSchema = z.object({
    /** The resolved ERCOT ESI ID, shown as "this is your meter". Display-only (AC3). */
    esiid: z.string().min(10).max(40),
    /** 5-digit ZIP of the premise (convenience mirror of `address.zip`). */
    zip: z.string().regex(/^\d{5}$/u, 'ZIP must be 5 digits'),
    /** Structured premise address. */
    address: MeterAddressSchema,
    /** Residential vs business (residential-only shopping today). */
    customerType: CustomerType,
    /** ERCOT annualized usage estimate (kWh/yr) — pre-fills the "how much do you use" step. */
    estimatedAnnualUsageKwh: z.number().int().nonnegative(),
    /** TDU display name for the premise (e.g. "Oncor"). */
    tdsp: z.string().min(1),
    /**
     * TDU CODE for the premise (e.g. "ONCR") — the machine key that matches a
     * tariff's `tdsp` code on `/bff/plans`. `tdsp` above is the human display name;
     * this is what the meter-filtered shelf (SHOP-5A) filters on, so the SPA never
     * has to reconcile a display name back to a code.
     */
    tduCode: z.string().min(1),
    /** ERCOT settlement/load zone. */
    loadZone: LoadZone,
    /** Physical meter class. */
    meterType: MeterType,
    /** Whether the meter is an Advanced Metering System (smart) meter. */
    isSmartMeter: z.boolean(),
    /** Premise service status. */
    serviceStatus: ServiceStatus,
    /** SWITCH (active incumbent) vs MOVE_IN (no active service). Derived server-side. */
    enrollmentType: EnrollmentType,
    /**
     * ⚠️ TRUE WHEN `address` IS AN EXAMPLE, NOT THIS METER'S REAL PREMISE (E-SF19).
     *
     * The dev ESI resolver synthesises a meter for ANY well-formed known-prefix ESI
     * ID, including one nobody has ever seeded. For those it has no premise to
     * report, so it substitutes a generic street in the TDU's default city — and the
     * card rendered that under the heading "Meter details confirmed", with nothing to
     * say it was a stand-in. `STUBS.md` promised such data would be badged; it was
     * not.
     *
     * When this is true the SPA MUST badge the address as an example and must not
     * present it as the confirmed service address. Absent/false means the address
     * came from the premise record.
     */
    addressIsPlaceholder: z.boolean().optional(),
});
/**
 * `POST /bff/esiid/lookup` request body. The ESI ID is a display/lookup input;
 * ERCOT ESI IDs are commonly 17 digits, the industry spec allows 10..22. The
 * caller may pass a raw value with spaces/hyphens (from a bill) — the SPA strips
 * to digits before sending, and the BFF re-validates.
 */
export const EsiidLookupRequestSchema = z.object({
    esiid: z.string().regex(/^\d{10,22}$/u, 'ESI ID must be 10–22 digits'),
});
/**
 * The lookup outcome, as a discriminated union. Only `resolved | not_found`
 * apply to a raw ESI ID (there is no ambiguous/out-of-territory outcome — an
 * ESI ID either belongs to a serviceable meter or it does not):
 *
 *   - `resolved`  — an opaque `candidateId` (the redemption handle) + the
 *                   display `MeterDetail`.
 *   - `not_found` — no meter for this ESI ID; the SPA offers "try ZIP instead".
 */
export const EsiidLookupResponseSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('resolved'),
        /** Opaque, session-scoped handle. Redeemed to the real ESI ID server-side (AC3). */
        candidateId: z.string().min(1),
        meter: MeterDetailSchema,
    }),
    z.object({
        outcome: z.literal('not_found'),
    }),
]);
//# sourceMappingURL=meter.js.map